import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CategoryCard from "../../components/CategoryCard.jsx";
import CourseCard from "../../components/CourseCard";
import ComponentCarousel from "../../components/utils/Carousel.jsx";
import {
  getAdminSuggestions,
  getCourseCategories,
  getFeaturedCourses,
  getIncompleteExams,
  getSavedCourses,
  putToggleBookmark,
} from "../../services/courseServices";
import { getBottomBanner, getMainBanner } from "../../services/dashboardService.js";
import { AdminDashboardWrapper } from "../../style/Admin/ManageDashboardStyle.jsx";

import "react-loading-skeleton/dist/skeleton.css";
import BottomBannerSkeleton from "../../components/skeleton/BottomBannerSkeleton";
import CategoryCardSkeleton from "../../components/skeleton/CategoryCardSkeleton";
import CourseCardSkeleton from "../../components/skeleton/CourseCardSkeleton";
import MainCarouselSkeleton from "../../components/skeleton/MainCarouselSkeleton";

function UserDashboard() {
  const [carouselImages, setCarouselImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);

  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [incompleteCourses, setIncompleteCourses] = useState([]);
  const [recommendedCourses, setRecommendedCourses] = useState([]);
  const [savedCourses, setSavedCourses] = useState([]);
  const [courseCategories, setCourseCategories] = useState([]);

  const [loading, setLoading] = useState({
    featured: true,
    incomplete: true,
    recommended: true,
    saved: true,
    categories: true,
    mainBanner: true,
    bottomBanner: true,
  });

  const [bottomBanner, setBottomBanner] = useState({
    title: "Upgrade to Premium",
    description: "Get unlimited access to all courses and premium features",
    image: "",
    href: "#",
    gradientFrom: "#4CAF50",
    gradientTo: "#2196F3",
    textColor: "#FFFFFF",
  });

  const updateLoadingState = useCallback((key, value) => {
    setLoading((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  }, []);

  // API calls
  const fetchFeaturedCourses = useCallback(async () => {
    updateLoadingState("featured", true);
    try {
      const response = await getFeaturedCourses();
      updateLoadingState("featured", false);
      return response.success ? response.data.courses || [] : [];
    } catch (error) {
      console.error("Error fetching popular courses:", error);
      updateLoadingState("featured", false);
      return [];
    }
  }, [updateLoadingState]);

  const fetchIncompleteExams = useCallback(async () => {
    updateLoadingState("incomplete", true);
    try {
      const response = await getIncompleteExams();
      updateLoadingState("incomplete", false);
      return response.success ? response.data.courses || [] : [];
    } catch (error) {
      console.error("Error fetching incomplete exams:", error);
      updateLoadingState("incomplete", false);
      return [];
    }
  }, [updateLoadingState]);

  const fetchRecommendedCourses = useCallback(async () => {
    updateLoadingState("recommended", true);
    try {
      const response = await getAdminSuggestions();
      updateLoadingState("recommended", false);
      return response.success ? response.data.courses || [] : [];
    } catch (error) {
      console.error("Error fetching recommended courses:", error);
      updateLoadingState("recommended", false);
      return [];
    }
  }, [updateLoadingState]);

  const fetchSavedCourses = useCallback(async () => {
    updateLoadingState("saved", true);
    try {
      const payload = {
        offset: 10,
        page: 0,
        search: "",
      };
      const response = await getSavedCourses(payload);
      updateLoadingState("saved", false);
      return response.success ? response.data.courses || [] : [];
    } catch (error) {
      console.error("Error fetching saved courses:", error);
      updateLoadingState("saved", false);
      return [];
    }
  }, [updateLoadingState]);

  const fetchCourseCategories = useCallback(async () => {
    updateLoadingState("categories", true);
    try {
      const response = await getCourseCategories();
      updateLoadingState("categories", false);
      return response.success ? response.data || [] : [];
    } catch (error) {
      console.error("Error fetching course categories:", error);
      updateLoadingState("categories", false);
      return [];
    }
  }, [updateLoadingState]);

  const fetchMainBanner = useCallback(async () => {
    updateLoadingState("mainBanner", true);
    try {
      const response = await getMainBanner();
      updateLoadingState("mainBanner", false);
      if (response.success && response.data) {
        return response.data.map((banner) => banner.details);
      }
      return [];
    } catch (error) {
      console.error("Error fetching main banners:", error);
      updateLoadingState("mainBanner", false);
      return [];
    }
  }, [updateLoadingState]);

  const fetchBottomBannerData = useCallback(async () => {
    updateLoadingState("bottomBanner", true);
    try {
      const response = await getBottomBanner();
      updateLoadingState("bottomBanner", false);
      if (response.success && response.data) {
        const bannerData = response.data.details;
        return {
          title: bannerData.title || "Upgrade to Premium",
          description: bannerData.description || "Get unlimited access to all courses and premium features",
          image: bannerData.url || "",
          href: bannerData.href || "#",
          gradientFrom: bannerData.gradientFrom || "#4CAF50",
          gradientTo: bannerData.gradientTo || "#2196F3",
          textColor: bannerData.textColor || "#FFFFFF",
        };
      }
      return null;
    } catch (error) {
      console.error("Error fetching bottom banner:", error);
      updateLoadingState("bottomBanner", false);
      return null;
    }
  }, [updateLoadingState]);

  // actions
  const toggleBookmark = useCallback(
    async (courseId, currentSaved) => {
      const updateCourseList = (courseList) => courseList.map((course) => (course.id === courseId ? { ...course, saved: !currentSaved } : course));

      setFeaturedCourses(updateCourseList(featuredCourses));
      setIncompleteCourses(updateCourseList(incompleteCourses));
      setRecommendedCourses(updateCourseList(recommendedCourses));

      if (currentSaved) {
        setSavedCourses(savedCourses.filter((course) => course.id !== courseId));
      } else {
        setSavedCourses(updateCourseList(savedCourses));
      }

      try {
        const payload = { save: !currentSaved };
        await putToggleBookmark({ payload, courseId });
      } catch (error) {
        const restoreCourseList = (courseList) => courseList.map((course) => (course.id === courseId ? { ...course, saved: currentSaved } : course));

        console.error("Error toggling bookmark:", error);
        toast.error("Unable to Save Course!");

        setFeaturedCourses(restoreCourseList(featuredCourses));
        setIncompleteCourses(restoreCourseList(incompleteCourses));
        setRecommendedCourses(restoreCourseList(recommendedCourses));

        if (currentSaved) {
          setSavedCourses([
            ...savedCourses,
            featuredCourses.find((c) => c.id === courseId) ||
              incompleteCourses.find((c) => c.id === courseId) ||
              recommendedCourses.find((c) => c.id === courseId),
          ]);
        } else {
          setSavedCourses(restoreCourseList(savedCourses));
        }
      }
    },
    [featuredCourses, incompleteCourses, recommendedCourses, savedCourses]
  );

  const handlePrevImage = useCallback(() => {
    setIsAutoScrolling(false);
    setCurrentImageIndex((prevIndex) => (prevIndex === 0 ? carouselImages.length - 1 : prevIndex - 1));
    setTimeout(() => setIsAutoScrolling(true), 5000);
  }, [carouselImages.length]);

  const handleNextImage = useCallback(() => {
    setIsAutoScrolling(false);
    setCurrentImageIndex((prevIndex) => (prevIndex === carouselImages.length - 1 ? 0 : prevIndex + 1));
    setTimeout(() => setIsAutoScrolling(true), 5000);
  }, [carouselImages.length]);

  const handleMouseEnter = useCallback(() => {
    setIsAutoScrolling(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsAutoScrolling(true);
  }, []);

  useEffect(() => {
    const getAllApiData = async () => {
      try {
        const [
          featuredCoursesData,
          incompleteExamsData,
          recommendedCoursesData,
          savedCoursesData,
          mainBannerImages,
          bottomBannerData,
          categoriesData,
        ] = await Promise.all([
          fetchFeaturedCourses(),
          fetchIncompleteExams(),
          fetchRecommendedCourses(),
          fetchSavedCourses(),
          fetchMainBanner(),
          fetchBottomBannerData(),
          fetchCourseCategories(),
        ]);

        setFeaturedCourses(featuredCoursesData);
        setIncompleteCourses(incompleteExamsData);
        setRecommendedCourses(recommendedCoursesData);
        setSavedCourses(savedCoursesData);
        setCarouselImages(mainBannerImages);
        setCourseCategories(categoriesData);

        if (bottomBannerData) {
          setBottomBanner(bottomBannerData);
        }
      } catch (error) {
        console.error("Error loading dashboard data:", error);
        toast.error("Failed to load some dashboard data");
      }
    };

    getAllApiData();
  }, [
    fetchFeaturedCourses,
    fetchIncompleteExams,
    fetchRecommendedCourses,
    fetchSavedCourses,
    fetchMainBanner,
    fetchBottomBannerData,
    fetchCourseCategories,
  ]);

  useEffect(() => {
    let intervalId;

    if (carouselImages.length > 1 && isAutoScrolling) {
      intervalId = setInterval(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex === carouselImages.length - 1 ? 0 : prevIndex + 1));
      }, 2000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [carouselImages.length, isAutoScrolling]);

  const renderCourseCards = useCallback((courses, onToggleBookmark) => {
    if (!Array.isArray(courses) || courses.length === 0) {
      return null;
    }
    return courses.map((course) => <CourseCard key={course.id} course={course} onToggleBookmark={() => onToggleBookmark(course.id, course.saved)} />);
  }, []);

  const renderCourseSkeletons = useCallback((count = 4) => {
    return Array(count)
      .fill()
      .map((_, index) => <CourseCardSkeleton key={index} />);
  }, []);

  const renderCategoryCards = useCallback((categories) => {
    if (!Array.isArray(categories) || categories.length === 0) {
      return null;
    }
    return categories.map((category) => <CategoryCard key={category.id} category={category} />);
  }, []);

  const renderCategorySkeletons = useCallback((count = 5) => {
    return Array(count)
      .fill()
      .map((_, index) => <CategoryCardSkeleton key={index} />);
  }, []);

  const featuredCourseComponents = useMemo(() => {
    return loading.featured ? renderCourseSkeletons(4) : renderCourseCards(featuredCourses, toggleBookmark);
  }, [featuredCourses, renderCourseCards, toggleBookmark, loading.featured, renderCourseSkeletons]);

  const incompleteExamCourseComponents = useMemo(() => {
    return loading.incomplete ? renderCourseSkeletons(4) : renderCourseCards(incompleteCourses, toggleBookmark);
  }, [incompleteCourses, renderCourseCards, toggleBookmark, loading.incomplete, renderCourseSkeletons]);

  const recommendedCourseComponents = useMemo(() => {
    return loading.recommended ? renderCourseSkeletons(4) : renderCourseCards(recommendedCourses, toggleBookmark);
  }, [recommendedCourses, renderCourseCards, toggleBookmark, loading.recommended, renderCourseSkeletons]);

  const savedCourseComponents = useMemo(() => {
    return loading.saved ? renderCourseSkeletons(4) : renderCourseCards(savedCourses, toggleBookmark);
  }, [savedCourses, renderCourseCards, toggleBookmark, loading.saved, renderCourseSkeletons]);

  const categoryComponents = useMemo(() => {
    return loading.categories ? renderCategorySkeletons(6) : renderCategoryCards(courseCategories);
  }, [courseCategories, renderCategoryCards, loading.categories, renderCategorySkeletons]);

  const carouselIndicators = useMemo(
    () => (
      <div className="carousel-indicators">
        {carouselImages.map((_, index) => (
          <span
            key={index}
            className={`indicator ${index === currentImageIndex ? "active" : ""}`}
            onClick={() => {
              setCurrentImageIndex(index);
              setIsAutoScrolling(false);
              setTimeout(() => setIsAutoScrolling(true), 5000);
            }}
          />
        ))}
      </div>
    ),
    [carouselImages, currentImageIndex]
  );

  const carouselSlider = useMemo(
    () =>
      carouselImages.length > 0 && (
        <div className="carousel-slider" style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}>
          {carouselImages.map((image, index) => (
            <div key={index} className="carousel-slide">
              <Link to={image.href}>
                <img src={image.url} alt={`Carousel image ${index + 1}`} className="carousel-image" loading="lazy" />
              </Link>
            </div>
          ))}
        </div>
      ),
    [carouselImages, currentImageIndex]
  );

  const bottomBannerSection = useMemo(
    () => (
      <div
        className="ad-banner-container"
        style={{
          background: `linear-gradient(135deg, ${bottomBanner.gradientFrom} 0%, ${bottomBanner.gradientTo} 100%)`,
        }}
      >
        <div className="ad-banner-content" style={{ color: bottomBanner.textColor }}>
          <h2>{bottomBanner.title}</h2>
          <p>{bottomBanner.description}</p>
          <a
            href={bottomBanner.href}
            className="ad-banner-button"
            style={{
              backgroundColor: bottomBanner.textColor,
              color: bottomBanner.gradientFrom,
            }}
          >
            Learn More
          </a>
        </div>
        <div className="ad-banner-image">{bottomBanner.image && <img src={bottomBanner.image} alt="Premium Membership" loading="lazy" />}</div>
      </div>
    ),
    [bottomBanner]
  );

  return (
    <AdminDashboardWrapper>
      <ToastContainer />

      <>
        {/* Main Carousel */}
        {loading.mainBanner ? (
          <MainCarouselSkeleton />
        ) : (
          <div className="carousel-wrapper">
            <div className="carousel-container">
              <div className="carousel-content" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                <button className="carousel-nav-btn prev-btn" onClick={handlePrevImage}>
                  <ChevronLeft />
                </button>

                <div className="carousel-slider-container">{carouselSlider}</div>

                <button className="carousel-nav-btn next-btn" onClick={handleNextImage}>
                  <ChevronRight />
                </button>
              </div>

              {carouselIndicators}
            </div>
          </div>
        )}

        {/* Incomplete Exam */}
        {loading.incomplete ? (
          <ComponentCarousel components={renderCourseSkeletons(4)} title="Incomplete Exams" itemWidth={300} gap={15} />
        ) : (
          incompleteCourses.length > 0 && (
            <ComponentCarousel components={incompleteExamCourseComponents} title="Incomplete Exams" itemWidth={300} gap={15} />
          )
        )}

        {/* Categories */}
        {loading.categories ? (
          <ComponentCarousel components={renderCategorySkeletons(5)} title="Course Categories" itemWidth={250} gap={10} />
        ) : (
          courseCategories.length > 0 && <ComponentCarousel components={categoryComponents} title="Course Categories" itemWidth={250} gap={10} />
        )}

        {/* Recommended by Admin */}
        {loading.recommended ? (
          <ComponentCarousel components={renderCourseSkeletons(4)} title="Recommended For You" itemWidth={300} gap={15} />
        ) : (
          recommendedCourses.length > 0 && (
            <ComponentCarousel components={recommendedCourseComponents} title="Recommended For You" itemWidth={300} gap={15} />
          )
        )}

        {/* Bottom Banner */}
        {loading.bottomBanner ? <BottomBannerSkeleton /> : bottomBannerSection}

        {/* Featured courses */}
        {loading.featured ? (
          <ComponentCarousel components={renderCourseSkeletons(4)} title="Featured Courses" itemWidth={300} gap={15} />
        ) : (
          featuredCourses.length > 0 && <ComponentCarousel components={featuredCourseComponents} title="Featured Courses" itemWidth={300} gap={15} />
        )}

        {/* Saved Courses */}
        {loading.saved ? (
          <ComponentCarousel components={renderCourseSkeletons(4)} title="Saved Courses" itemWidth={300} gap={15} />
        ) : (
          savedCourses.length > 0 && <ComponentCarousel components={savedCourseComponents} title="Saved Courses" itemWidth={300} gap={15} />
        )}
      </>
    </AdminDashboardWrapper>
  );
}

export default UserDashboard;
